import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "react-query";

import { LicensesService } from "~/entity/Licenses/api/service";
import { useAddNotificationMutation } from "~/schemas/generated/graphql";
import { useNotify } from "~/shared/hooks/useNotify";
import { defaultErrorHandlerFunc } from "~/shared/hooks/useRequestHandler";

import { submitLicenseAssign, submitLicenseRevoke } from "./submit";
import { ILicenseAssignPayload, ILicenseRevokePayload } from "./types";

export const useLicenseActionsSubmit = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [addNotificationMutation] = useAddNotificationMutation({});

  const { notifyWarning, notifySuccess } = useNotify();

  const invalidateLicenses = () =>
    queryClient.invalidateQueries([LicensesService._routes.licenses]);

  const assignLicenses = useMutation(
    (payload: ILicenseAssignPayload) => submitLicenseAssign(payload),
    {
      onSuccess: ({ assigned, failed }) => {
        if (assigned === 0 && failed === 0) {
          notifyWarning(t("licenseActionsModals.notification.assignEmpty"));
          return;
        }

        if (failed > 0) {
          notifyWarning(
            t("licenseActionsModals.notification.assignPartial", {
              assigned,
              failed,
            }),
          );
        } else {
          notifySuccess(t("licenseActionsModals.notification.assignSuccess"));
        }
        invalidateLicenses();
      },
      onError: (e) => {
        defaultErrorHandlerFunc({
          e,
          t,
          addNotificationMutation,
        });
      },
    },
  );

  const revokeLicenses = useMutation(
    (payload: ILicenseRevokePayload) => submitLicenseRevoke(payload),
    {
      onSuccess: ({ revoked, failed }) => {
        if (revoked === 0 && failed === 0) {
          notifyWarning(t("licenseActionsModals.notification.revokeEmpty"));
          return;
        }

        if (failed > 0) {
          notifyWarning(
            t("licenseActionsModals.notification.revokePartial", {
              revoked,
              failed,
            }),
          );
        } else {
          notifySuccess(t("licenseActionsModals.notification.revokeSuccess"));
        }

        if (revoked > 0) {
          invalidateLicenses();
        }
      },
      onError: (e) => {
        defaultErrorHandlerFunc({
          e,
          t,
          addNotificationMutation,
        });
      },
    },
  );

  return {
    assignLicenses,
    revokeLicenses,
  };
};
